import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { BookDirectory, BookListInfo, ScanResult, JsonDirectory } from '../types/directory';
import { COMMON_BOOK_LIST_NAMES, BACKEND_BASE_URL, STORAGE_KEYS, API_ENDPOINTS } from '../config/directory';
import { AppInitializer } from '../services/AppInitializer';
import { persistentLogger } from '../utils/PersistentLogger';
import { ListService, type ListInfo } from '../services/ListService';
import { settingsService } from '../services/SettingsService';
import { useAuth } from '../contexts/AuthContext';
import { FolderService } from '../services/FolderService';

// Types
interface BookFileSelectorProps {
  onBookFileSelected: (bookId: string, bookDirectoryPath?: string) => void;
  currentBookId?: string;
  isS3Mode?: boolean;
}

const BookFileSelector: React.FC<BookFileSelectorProps> = ({ 
  onBookFileSelected, 
  currentBookId,
  isS3Mode = false
}) => {
  console.log('[BookFileSelector] Component rendering, isS3Mode prop:', isS3Mode);
  const { hasAccessToFolder, allowedFolders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [directories, setDirectories] = useState<BookDirectory[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDirectoryPath, setNewDirectoryPath] = useState('');
  const [newDirectoryName, setNewDirectoryName] = useState('');
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [selectedS3Folder, setSelectedS3Folder] = useState<string>('practice');
  const [isS3FolderOpen, setIsS3FolderOpen] = useState(false);
  const [availableLists, setAvailableLists] = useState<ListInfo[]>([]);
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [isLoadingLists, setIsLoadingLists] = useState(false);
  const s3FolderDropdownRef = useRef<HTMLDivElement>(null);

  // S3 folder options - filtered based on user's access level
  const allS3FolderOptions = [
    { value: 'practice', label: 'Practice & Development Area' },
    { value: 'books', label: 'Real Books (Only use if you are ready)' }
  ];
  
  // Filter options based on user's allowed folders
  const s3FolderOptions = allS3FolderOptions.filter(option => hasAccessToFolder(option.value));

  // Filter local directories based on user's access level
  // Path must contain one of the allowed folder names as a substring
  const filteredDirectories = useMemo(() => {
    if (!allowedFolders) return directories; // No restrictions if no allowedFolders set
    
    return directories.filter(dir => {
      const pathLower = dir.path.toLowerCase();
      return allowedFolders.some(folder => pathLower.includes(folder.toLowerCase()));
    });
  }, [directories, allowedFolders]);

  // Track if app initialization is complete
  const [initComplete, setInitComplete] = useState(false);
  
  // Load directories on mount
  useEffect(() => {
    console.log('[BookFileSelector] Initial useEffect running, isS3Mode:', isS3Mode);
    loadStoredDirectories();
    
    // Initialize dropdown from FolderService if in S3 mode
    if (isS3Mode) {
      const currentFolder = FolderService.getFolderIfSet();
      if (currentFolder && (currentFolder === 'books' || currentFolder === 'practice')) {
        persistentLogger.log('BookFileSelector', 'Initializing dropdown from FolderService', { currentFolder });
        setSelectedS3Folder(currentFolder);
      } else {
        // Fall back to localStorage
        const stored = localStorage.getItem('selected-s3-folder');
        if (stored && (stored === 'books' || stored === 'practice')) {
          persistentLogger.log('BookFileSelector', 'Initializing dropdown from localStorage', { stored });
          setSelectedS3Folder(stored);
        }
      }
    }
    
    // Wait for app initialization to complete
    const unsubscribe = AppInitializer.onInitComplete(() => {
      console.log('[BookFileSelector] App initialization complete, ready to initialize URL params');
      setInitComplete(true);
      
      // Sync dropdown with FolderService after init
      if (isS3Mode) {
        const currentFolder = FolderService.getFolderIfSet();
        if (currentFolder && (currentFolder === 'books' || currentFolder === 'practice')) {
          persistentLogger.log('BookFileSelector', 'Syncing dropdown after init complete', { currentFolder });
          setSelectedS3Folder(currentFolder);
        }
      }
    });
    
    // If already initialized, set immediately
    if (AppInitializer.isInitialized()) {
      setInitComplete(true);
      // Also sync dropdown
      if (isS3Mode) {
        const currentFolder = FolderService.getFolderIfSet();
        if (currentFolder && (currentFolder === 'books' || currentFolder === 'practice')) {
          persistentLogger.log('BookFileSelector', 'Syncing dropdown (already initialized)', { currentFolder });
          setSelectedS3Folder(currentFolder);
        }
      }
    }
    
    // Listen for custom event to trigger directory selection from hero
    const handleTriggerDirectorySelect = () => {
      setShowAddForm(true);
    };
    
    window.addEventListener('triggerDirectorySelect', handleTriggerDirectorySelect);
    
    return () => {
      unsubscribe();
      window.removeEventListener('triggerDirectorySelect', handleTriggerDirectorySelect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load available lists when folder changes (both S3 and local mode)
  useEffect(() => {
    const loadLists = async () => {
      if (!initComplete) return;
      
      try {
        const folder = FolderService.getFolderIfSet();
        if (!folder) return;

        setIsLoadingLists(true);
        const lists = await ListService.getAvailableLists();
        setAvailableLists(lists);
        
        // Initialize selectedList from URL params or settings
        const urlList = searchParams.get('list');
        const savedList = settingsService.getSelectedList();
        const initialList = urlList || savedList || null;
        
        if (initialList) {
          setSelectedList(initialList);
          if (urlList) {
            // URL param takes precedence - update settings
            settingsService.setSelectedList(initialList);
          }
        } else if (lists.length > 0) {
          // If lists exist but none selected, set to null (will require selection)
          setSelectedList(null);
          settingsService.setSelectedList(null);
        } else {
          // No lists available, clear selection
          setSelectedList(null);
          settingsService.setSelectedList(null);
        }
      } catch (error) {
        console.error('[BookFileSelector] Error loading lists:', error);
        setAvailableLists([]);
      } finally {
        setIsLoadingLists(false);
      }
    };

    loadLists();

    // Also reload when folder changes
    const handleFolderChange = () => {
      // Clear list selection when folder changes (lists are folder-specific)
      setSelectedList(null);
      settingsService.setSelectedList(null);
      // Remove list from URL
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('list');
        return newParams;
      });
      loadLists();
    };

    window.addEventListener('folderChanged', handleFolderChange);
    
    return () => {
      window.removeEventListener('folderChanged', handleFolderChange);
    };
  }, [searchParams, initComplete]);

  // Track if we're currently updating URL to prevent loops
  const isUpdatingUrlRef = useRef(false);
  
  // Track if auto-selection has been attempted
  const autoSelectAttemptedRef = useRef(false);

  // Handle click outside S3 folder dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (s3FolderDropdownRef.current && !s3FolderDropdownRef.current.contains(event.target as Node)) {
        setIsS3FolderOpen(false);
      }
    };

    if (isS3FolderOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isS3FolderOpen]);
  
  // Define processBookList early so it can be used in useEffects
  const processBookList = useCallback(async (directoryId: string, bookList: BookListInfo, directoryPath: string) => {
    try {
      // Check if user is authenticated before proceeding
      const { AuthService } = await import('../services/AuthService');
      const isAuthenticated = AuthService.isAuthenticated();
      const hasToken = !!AuthService.getToken();
      
      persistentLogger.log('BookFileSelector', 'processBookList() called', {
        directoryId,
        bookListFileName: bookList.fileName,
        isAuthenticated,
        hasToken
      });
      
      if (!isAuthenticated || !hasToken) {
        persistentLogger.warn('BookFileSelector', 'processBookList() - User not authenticated, delaying execution');
        // Wait a bit for auth to be ready (in case we just logged in)
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Check again
        const stillAuthenticated = AuthService.isAuthenticated();
        const stillHasToken = !!AuthService.getToken();
        persistentLogger.log('BookFileSelector', 'processBookList() - Re-checking auth after delay', {
          stillAuthenticated,
          stillHasToken
        });
        
        if (!stillAuthenticated || !stillHasToken) {
          persistentLogger.error('BookFileSelector', 'processBookList() - Still not authenticated, aborting');
          alert('Please log in first');
          return;
        }
      }
      
      // Generate unique ID for this book list
      const listId = `list-${directoryId}-${bookList.fileName}`;
      
      // Update URL parameter with directory name (for local mode) - only if it's different
      if (!isS3Mode) {
        const directory = directories.find(d => d.id === directoryId);
        if (directory) {
          // Save as last selected directory
          localStorage.setItem(STORAGE_KEYS.LAST_SELECTED_DIRECTORY, directory.name);
          
          // Set FolderService with the full directory path for local mode
          // IMPORTANT: FolderService must be set BEFORE onBookFileSelected
          // This ensures the folderChanged event (which clears currentBookId) fires
          // before we set the new currentBookId
          const currentFolder = FolderService.getFolder();
          if (currentFolder !== directoryPath) {
            persistentLogger.log('BookFileSelector', 'Setting FolderService with directory path', {
              currentFolder,
              newFolder: directoryPath,
              directoryName: directory.name
            });
            FolderService.setFolder(directoryPath);
            
            // Clear list selection when folder changes (lists are folder-specific)
            setSelectedList(null);
            settingsService.setSelectedList(null);
          } else {
            persistentLogger.log('BookFileSelector', 'FolderService already set with this directory path', {
              folder: directoryPath
            });
          }
          
          const currentParam = new URLSearchParams(window.location.search).get('books-to-edit');
          if (currentParam !== directory.name) {
            // Set flag to prevent main effect from interfering
            isUpdatingUrlRef.current = true;
            setSearchParams(prev => {
              const newParams = new URLSearchParams(prev);
              newParams.set('books-to-edit', directory.name);
              // Remove list param when folder changes (lists are folder-specific)
              newParams.delete('list');
              return newParams;
            });
            navigate(`${location.pathname}?books-to-edit=${encodeURIComponent(directory.name)}`, { replace: true });
            // Reset flag after navigation
            setTimeout(() => {
              isUpdatingUrlRef.current = false;
            }, 100);
          }
        }
      }
      
      // Select the book list with directory path
      persistentLogger.log('BookFileSelector', 'processBookList() - Calling onBookFileSelected', { listId, directoryPath });
      onBookFileSelected(listId, directoryPath);
      setIsOpen(false);
      
    } catch (error) {
      persistentLogger.error('BookFileSelector', 'processBookList() - Error', { error });
      console.error('Error processing book list:', error);
      alert('Failed to process book list. Please try again.');
    }
  }, [isS3Mode, directories, setSearchParams, onBookFileSelected, navigate, location.pathname]);
  
  // Main effect: Ensure URL parameter exists and sync everything
  useEffect(() => {
    // Don't run until app initialization is complete
    if (!initComplete) {
      return;
    }
    
    // Prevent re-running if we're in the middle of updating the URL
    if (isUpdatingUrlRef.current) {
      return;
    }
    
    const booksToEdit = searchParams.get('books-to-edit');
    console.log('[BookFileSelector] URL param check, isS3Mode:', isS3Mode, 'books-to-edit:', booksToEdit);
    
    if (isS3Mode) {
      // S3 Mode: URL param maps to 'real' or 'practice'
      if (booksToEdit) {
        // URL parameter exists - use it
        let folder = booksToEdit === 'real' ? 'books' : booksToEdit === 'practice' ? 'practice' : null;
        
        // Check if user has access to this folder
        if (folder && !hasAccessToFolder(folder)) {
          console.log('[BookFileSelector] S3 Mode - User does not have access to folder:', folder);
          persistentLogger.log('BookFileSelector', 'S3 Mode - Access denied to requested folder, redirecting', {
            requestedFolder: folder,
            allowedFolders
          });
          
          // Redirect to first allowed folder
          const firstAllowedFolder = s3FolderOptions.length > 0 ? s3FolderOptions[0].value : 'practice';
          const redirectParam = firstAllowedFolder === 'books' ? 'real' : 'practice';
          
          // Set FolderService IMMEDIATELY before redirect so book loading has the correct folder
          FolderService.setFolder(firstAllowedFolder);
          persistentLogger.log('BookFileSelector', 'S3 Mode - Set FolderService during access redirect', {
            folder: firstAllowedFolder
          });
          
          // Also save to localStorage and update state
          saveSelectedS3Folder(firstAllowedFolder);
          setSelectedS3Folder(firstAllowedFolder);
          
          isUpdatingUrlRef.current = true;
          setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.set('books-to-edit', redirectParam);
            return newParams;
          });
          navigate(`${location.pathname}?books-to-edit=${redirectParam}`, { replace: true });
          setTimeout(() => {
            isUpdatingUrlRef.current = false;
          }, 100);
          
          folder = firstAllowedFolder;
        }
        
        if (folder) {
          console.log('[BookFileSelector] S3 Mode - URL parameter found:', booksToEdit, '-> folder:', folder);
          persistentLogger.log('BookFileSelector', 'S3 Mode - URL parameter found, initializing FolderService', {
            booksToEdit,
            folder,
            currentDropdownState: selectedS3Folder
          });
          // Save to localStorage
          saveSelectedS3Folder(folder);
          // Update state
          setSelectedS3Folder(folder);
          
          // Check if FolderService is already initialized with this folder
          // IMPORTANT: Do this SYNCHRONOUSLY to avoid race conditions
          const currentFolder = FolderService.getFolder();
          if (currentFolder !== folder) {
            persistentLogger.log('BookFileSelector', 'Initializing FolderService from URL parameter', {
              currentFolder,
              newFolder: folder
            });
            FolderService.setFolder(folder);
          } else {
            persistentLogger.log('BookFileSelector', 'FolderService already initialized with this folder', {
              folder
            });
          }
        }
      } else {
        // No URL parameter - need to add one
        console.log('[BookFileSelector] S3 Mode - No URL parameter, checking FolderService and localStorage...');
        
        // Check FolderService (might be initialized by AppInitializer) and localStorage
        // IMPORTANT: Do this SYNCHRONOUSLY to avoid race conditions with BookList
        const folderFromService = FolderService.getFolderIfSet();
        const stored = localStorage.getItem('selected-s3-folder');
        
        // Use FolderService value if available, otherwise localStorage, otherwise default
        // Default to first allowed folder, or 'practice' if none available
        const defaultFolder = s3FolderOptions.length > 0 ? s3FolderOptions[0].value : 'practice';
        const folderToUse = folderFromService || stored || defaultFolder;
        const urlParam = folderToUse === 'books' ? 'real' : 'practice';
        
        persistentLogger.log('BookFileSelector', 'S3 Mode - Adding URL parameter', {
          folderFromService,
          stored,
          folderToUse,
          urlParam,
          currentDropdownState: selectedS3Folder
        });
        
        // CRITICAL: Set FolderService IMMEDIATELY before navigation
        // This ensures BookList.autoLoadDefaultBookList() can find the folder
        FolderService.setFolder(folderToUse);
        persistentLogger.log('BookFileSelector', 'S3 Mode - FolderService set SYNCHRONOUSLY', {
          folder: folderToUse
        });
        
        // Update dropdown state to match
        if (folderToUse !== selectedS3Folder) {
          persistentLogger.log('BookFileSelector', 'Updating dropdown state to match folder', {
            from: selectedS3Folder,
            to: folderToUse
          });
          setSelectedS3Folder(folderToUse);
        }
        
        // Save to localStorage
        saveSelectedS3Folder(folderToUse);
        
        isUpdatingUrlRef.current = true;
        
        // Add URL parameter
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('books-to-edit', urlParam);
          return newParams;
        });
        
        // Navigate will trigger this effect to run again, which will then process the URL param
        navigate(`${location.pathname}?books-to-edit=${urlParam}`, { replace: true });
        
        // Reset flag after a brief delay to allow navigation to complete
        setTimeout(() => {
          isUpdatingUrlRef.current = false;
        }, 100);
      }
    } else {
      // Local Mode: URL param should be directory name
      if (booksToEdit) {
        // URL parameter exists - ensure FolderService is set with the directory path
        console.log('[BookFileSelector] Local Mode - URL parameter found:', booksToEdit);
        
        // Find the directory in the current directories list
        let directory = directories.find(d => d.name === booksToEdit);
        
        // If not found in current directories, check localStorage
        if (!directory) {
          try {
            const storedDirectoriesString = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
            if (storedDirectoriesString) {
              const storedDirectories: BookDirectory[] = JSON.parse(storedDirectoriesString);
              directory = storedDirectories.find(d => d.name === booksToEdit);
            }
          } catch (error) {
            console.error('[BookFileSelector] Error parsing stored directories:', error);
          }
        }
        
        // If directory found, ensure FolderService is set with its path
        if (directory) {
          const currentFolder = FolderService.getFolder();
          if (currentFolder !== directory.path) {
            persistentLogger.log('BookFileSelector', 'Local Mode - Setting FolderService from URL parameter', {
              directoryName: booksToEdit,
              directoryPath: directory.path,
              currentFolder
            });
            FolderService.setFolder(directory.path);
          } else {
            persistentLogger.log('BookFileSelector', 'Local Mode - FolderService already set with this directory path', {
              folder: directory.path
            });
          }
        } else {
          persistentLogger.warn('BookFileSelector', 'Local Mode - URL parameter directory not found in directories list', {
            booksToEdit,
            availableDirectories: directories.map(d => d.name)
          });
        }
      } else {
        // No URL parameter - check if we have directories to auto-select
        if (directories.length > 0) {
          const stored = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
          if (stored) {
            try {
              const savedDirs: BookDirectory[] = JSON.parse(stored);
              if (savedDirs.length > 0) {
                // Try to use last selected directory, otherwise use first
                const lastSelectedName = localStorage.getItem(STORAGE_KEYS.LAST_SELECTED_DIRECTORY);
                let dirToUse: BookDirectory | null = null;
                
                if (lastSelectedName) {
                  // Find directory by last selected name
                  dirToUse = savedDirs.find(d => d.name === lastSelectedName) || null;
                }
                
                // Fall back to first directory if last selected not found
                if (!dirToUse) {
                  dirToUse = savedDirs[0];
                }
                
                if (dirToUse && dirToUse.bookLists.length > 0) {
                  console.log('[BookFileSelector] Local Mode - Adding URL parameter for directory:', dirToUse.name, lastSelectedName ? '(last selected)' : '(first available)');
                  isUpdatingUrlRef.current = true;
                  
                  // Add URL parameter
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev);
                    newParams.set('books-to-edit', dirToUse!.name);
                    return newParams;
                  });
                  
                  navigate(`${location.pathname}?books-to-edit=${encodeURIComponent(dirToUse.name)}`, { replace: true });
                  
                  // Auto-select the first book list
                  processBookList(dirToUse.id, dirToUse.bookLists[0], dirToUse.path);
                  autoSelectAttemptedRef.current = true;
                  
                  setTimeout(() => {
                    isUpdatingUrlRef.current = false;
                  }, 100);
                }
              }
            } catch (error) {
              console.error('[BookFileSelector] Error parsing saved directories:', error);
            }
          }
        }
        // If no directories, don't add URL parameter - let the default page show
      }
    }
  }, [isS3Mode, searchParams, directories, navigate, setSearchParams, processBookList, location.pathname, initComplete, hasAccessToFolder, allowedFolders, s3FolderOptions]);

  // Sync dropdown with URL parameter changes (for S3 mode) - only when URL changes externally
  useEffect(() => {
    if (!isS3Mode || isUpdatingUrlRef.current) return;
    
    const booksToEdit = searchParams.get('books-to-edit');
    if (booksToEdit) {
      const folder = booksToEdit === 'real' ? 'books' : booksToEdit === 'practice' ? 'practice' : null;
      if (folder && folder !== selectedS3Folder) {
        console.log('[BookFileSelector] Syncing dropdown with URL parameter change:', booksToEdit, '->', folder);
        setSelectedS3Folder(folder);
        saveSelectedS3Folder(folder);
        // Also update FolderService to keep it in sync
        FolderService.setFolder(folder);
      }
    }
  }, [isS3Mode, searchParams, selectedS3Folder]);
  
  // Handle book list auto-selection for local mode when URL parameter exists
  useEffect(() => {
    if (isS3Mode || directories.length === 0 || autoSelectAttemptedRef.current || isUpdatingUrlRef.current) return;
    
    // Only auto-select if URL parameter exists and we don't have a current book
    if (!currentBookId) {
      const booksToEdit = searchParams.get('books-to-edit');
      
      if (booksToEdit) {
        // URL parameter exists - find directory by name and auto-select first book list
        const directory = directories.find(d => d.name === booksToEdit);
        if (directory && directory.bookLists.length > 0) {
          console.log('[BookFileSelector] Local Mode - Auto-selecting book list from URL param:', booksToEdit);
          processBookList(directory.id, directory.bookLists[0], directory.path);
          autoSelectAttemptedRef.current = true;
        }
      }
      // If no URL parameter, the main effect will handle adding it
    }
  }, [directories, isS3Mode, currentBookId, searchParams, processBookList]);

  // No useEffect watching URL changes for S3 mode - user changes folder directly via dropdown
  // URL parameter is only checked on initial load (in the useEffect above)

  const loadSelectedS3Folder = (): void => {
    try {
      // Don't check URL parameter here - let the useEffect handle it
      // Just load from localStorage as the initial value
      const stored = localStorage.getItem('selected-s3-folder');
      if (stored) {
        setSelectedS3Folder(stored);
        // Update FolderService with stored folder
        FolderService.setFolder(stored);
      }
    } catch (error) {
      console.error('Failed to load selected S3 folder:', error);
    }
  };

  const saveSelectedS3Folder = (folder: string) => {
    try {
      localStorage.setItem('selected-s3-folder', folder);
    } catch (error) {
      console.error('Failed to save selected S3 folder:', error);
    }
  };

  const loadStoredDirectories = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
      if (stored) {
        setDirectories(JSON.parse(stored));
      }
    } catch (error) {
      console.error('Failed to load stored directories:', error);
    }
  };

  const saveStoredDirectories = (dirs: BookDirectory[]) => {
    try {
      localStorage.setItem(STORAGE_KEYS.BOOK_DIRECTORIES, JSON.stringify(dirs));
      setDirectories(dirs);
    } catch (error) {
      console.error('Failed to save stored directories:', error);
    }
  };

  // Utility functions
  const scanDirectory = async (directoryPath: string): Promise<ScanResult> => {
    try {
      const { authenticatedPost } = await import('../utils/api');
      const response = await authenticatedPost(`${API_ENDPOINTS.SCAN_DIRECTORY}`, { directoryPath });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to scan directory: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error scanning directory:', error);
      throw error;
    }
  };

  const generateDirectoryId = (name: string, timestamp?: number): string => {
    return `dir-${timestamp || Date.now()}-${name.replace(/[^a-zA-Z0-9]/g, '-')}`;
  };

  const validateDirectoryPath = (path: string): boolean => {
    return path.trim().length > 0;
  };

  const validateDirectoryName = (name: string): boolean => {
    return name.trim().length > 0;
  };

  const handleAddDirectory = async () => {
    if (!validateDirectoryPath(newDirectoryPath) || !validateDirectoryName(newDirectoryName)) {
      alert('Please enter both directory path and name.');
      return;
    }

    setIsScanning(true);
    try {
      const scanResult = await scanDirectory(newDirectoryPath.trim());
      
      if (scanResult.bookLists.length === 0 && scanResult.subdirectories.length === 0) {
        alert(`No book list files found in the specified directory.\n\nLooking for: ${COMMON_BOOK_LIST_NAMES.join(', ')}`);
        return;
      }

      const newDirectories = createDirectoryEntries(scanResult, newDirectoryName.trim());
      const updatedDirectories = [...newDirectories, ...directories];
      saveStoredDirectories(updatedDirectories);

      // Auto-select the first directory with books
      if (newDirectories.length > 0) {
        const firstDir = newDirectories[0];
        if (firstDir.bookLists.length > 0) {
          await processBookList(firstDir.id, firstDir.bookLists[0], firstDir.path);
        }
      }

      // Reset form
      resetAddForm();
      
    } catch (error) {
      console.error('Error scanning directory:', error);
      alert(`Failed to scan directory: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsScanning(false);
    }
  };

  const createDirectoryEntries = (scanResult: ScanResult, baseName: string): BookDirectory[] => {
    const directories: BookDirectory[] = [];
    
    // Add main directory
    directories.push({
      id: generateDirectoryId(baseName),
      name: baseName,
      path: scanResult.directoryPath,
      bookLists: scanResult.bookLists,
      addedAt: new Date().toISOString(),
    });
    
    // Add subdirectories
    scanResult.subdirectories.forEach(subdir => {
      directories.push({
        id: generateDirectoryId(`${baseName}-${subdir.name}`),
        name: `${baseName} - ${subdir.name}`,
        path: subdir.path,
        bookLists: subdir.bookLists,
        addedAt: new Date().toISOString(),
      });
    });
    
    return directories;
  };

  const resetAddForm = () => {
    setNewDirectoryPath('');
    setNewDirectoryName('');
    setShowAddForm(false);
  };

  const handleJsonImport = async () => {
    try {
      const file = await selectJsonFile();
      if (!file) return;

      const jsonData = await parseJsonFile(file);
      if (!isValidJsonFormat(jsonData)) {
        alert('Invalid JSON format. Expected format:\n\n{\n  "directories": [\n    {"path": "relative/path", "name": "Display Name"}\n  ]\n}');
        return;
      }

      const basePath = await promptForBasePath();
      if (basePath === null) return; // User cancelled

      setIsScanning(true);
      await importDirectoriesFromJson(jsonData.directories, basePath.trim());
      
    } catch (error) {
      console.error('Error importing JSON:', error);
      alert('Failed to import JSON file. Please try again.');
    }
  };

  const selectJsonFile = (): Promise<File | null> => {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.onchange = (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) {
          resolve(null);
          return;
        }
        
        if (!file.name.endsWith('.json')) {
          alert('Please select a JSON file.');
          resolve(null);
          return;
        }
        
        resolve(file);
      };
      input.click();
    });
  };

  const parseJsonFile = async (file: File): Promise<any> => {
    try {
      const content = await file.text();
      return JSON.parse(content);
    } catch (error) {
      console.error('Error reading JSON file:', error);
      throw new Error('Failed to read the JSON file. Please check the format.');
    }
  };

  const isValidJsonFormat = (jsonData: any): boolean => {
    return jsonData.directories && Array.isArray(jsonData.directories);
  };

  const promptForBasePath = (): Promise<string | null> => {
    return new Promise((resolve) => {
      const basePath = prompt(
        'Enter the base path for the relative paths in the JSON file:\n\n' +
        'Example: /Users/username/Documents/books\n\n' +
        'Leave empty if paths in JSON are already absolute.'
      );
      resolve(basePath);
    });
  };

  const importDirectoriesFromJson = async (jsonDirectories: JsonDirectory[], basePath: string) => {
    try {
      const results = await Promise.allSettled(
        jsonDirectories.map(dir => processJsonDirectory(dir, basePath))
      );

      const newDirectories: BookDirectory[] = [];
      let successCount = 0;
      let errorCount = 0;

      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          newDirectories.push(...result.value);
          successCount++;
        } else {
          errorCount++;
          console.error(`Error processing directory ${jsonDirectories[index].path}:`, result.status === 'rejected' ? result.reason : 'Unknown error');
        }
      });

      if (newDirectories.length > 0) {
        const updatedDirectories = [...newDirectories, ...directories];
        saveStoredDirectories(updatedDirectories);
        
        // Auto-select the first directory with books
        const firstDir = newDirectories[0];
        if (firstDir.bookLists.length > 0) {
          await processBookList(firstDir.id, firstDir.bookLists[0], firstDir.path);
        }
      }

      showImportResults(successCount, errorCount, newDirectories.length > 0);
      
    } catch (error) {
      console.error('Error importing directories:', error);
      alert(`Failed to import directories: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsScanning(false);
    }
  };

  const processJsonDirectory = async (dir: JsonDirectory, basePath: string): Promise<BookDirectory[] | null> => {
    try {
      const fullPath = basePath ? `${basePath}/${dir.path}` : dir.path;
      const scanResult = await scanDirectory(fullPath);
      
      if (scanResult.bookLists.length === 0 && scanResult.subdirectories.length === 0) {
        console.warn(`No book lists found in: ${fullPath}`);
        return null;
      }

      return createDirectoryEntries(scanResult, dir.name);
    } catch (error) {
      console.error(`Error scanning directory ${dir.path}:`, error);
      return null;
    }
  };

  const showImportResults = (successCount: number, errorCount: number, hasValidDirectories: boolean) => {
    const message = `Import completed!\n\n✅ Successfully imported: ${successCount} directories\n❌ Failed to import: ${errorCount} directories\n\n${hasValidDirectories ? 'First directory has been selected.' : 'No valid directories found.'}`;
    alert(message);
  };

  const handleDirectorySelect = (directory: BookDirectory) => {
    if (directory.bookLists.length === 0) {
      alert('No book lists found in this directory.');
      return;
    }

    // Auto-select the first book list
    const firstBookList = directory.bookLists[0];
    processBookList(directory.id, firstBookList, directory.path);
  };

  const handleBookListSelect = (directory: BookDirectory, bookList: BookListInfo) => {
    processBookList(directory.id, bookList, directory.path);
  };

  const handleDeleteDirectory = async (directoryId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (confirm('Are you sure you want to delete this directory?')) {
      try {
        // Remove from stored directories
        const updatedDirectories = directories.filter(d => d.id !== directoryId);
        saveStoredDirectories(updatedDirectories);
        
        // If this was the current directory, clear selection
        if (currentBookId && currentBookId.startsWith(`list-${directoryId}`)) {
          onBookFileSelected('');
        }
      } catch (error) {
        console.error('Error deleting directory:', error);
        alert('Failed to delete directory. Please try again.');
      }
    }
  };

  const handleRenameStart = (directoryId: string, currentName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setEditingName(directoryId);
    setEditingValue(currentName);
  };

  const handleRenameSave = (directoryId: string) => {
    if (editingValue.trim()) {
      const updatedDirectories = directories.map(dir => 
        dir.id === directoryId ? { ...dir, name: editingValue.trim() } : dir
      );
      saveStoredDirectories(updatedDirectories);
    }
    setEditingName(null);
    setEditingValue('');
  };

  const handleRenameCancel = () => {
    setEditingName(null);
    setEditingValue('');
  };

  const flushBookData = () => {
    // Clear book-related localStorage while preserving settings
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('book-list-') || 
        key === 'current-book-list-id' ||
        key.startsWith('book-spine-') ||
        key.startsWith('book-page-') ||
        key.startsWith('book-image-')
      )) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Reset pagination to page 1 for both tables
    const settingsService = (window as any).settingsService;
    if (settingsService) {
      settingsService.setInfoTablePageIndex(0);
      settingsService.setImagesTablePageIndex(0);
    }
    
    // Dispatch custom event to notify components to refresh
    window.dispatchEvent(new CustomEvent('s3FolderChanged'));
    
    console.log('Book data flushed, components notified to refresh');
  };

  const handleS3FolderChange = (folderValue: string) => {
    console.log('[BookFileSelector] S3 folder dropdown changed to:', folderValue);
    
    // Clear list selection when folder changes (lists are folder-specific)
    setSelectedList(null);
    settingsService.setSelectedList(null);
    
    // Update URL parameter first (this is the source of truth)
    const urlParam = folderValue === 'books' ? 'real' : 'practice';
    isUpdatingUrlRef.current = true;
    
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('books-to-edit', urlParam);
      // Remove list param when folder changes (lists are folder-specific)
      newParams.delete('list');
      return newParams;
    });
    
    navigate(`${location.pathname}?books-to-edit=${urlParam}`, { replace: true });
    
    // Update state and localStorage
    setSelectedS3Folder(folderValue);
    saveSelectedS3Folder(folderValue);
    
    // Update FolderService (this will clear all caches)
    FolderService.setFolder(folderValue);
    
    // Flush book data and notify components to refresh
    flushBookData();
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('s3FolderChanged'));
    
    // Reset flag after navigation
    setTimeout(() => {
      isUpdatingUrlRef.current = false;
    }, 100);
  };

  const handleListChange = async (listValue: string) => {
    console.log('[BookFileSelector] List dropdown changed to:', listValue);
    
    setSelectedList(listValue);
    settingsService.setSelectedList(listValue);
    
    // Update URL parameter
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (listValue && listValue !== '--none--') {
        newParams.set('list', listValue);
      } else {
        newParams.delete('list');
      }
      return newParams;
    });
    
    // Flush all book data to clear spine data and force reload
    flushBookData();
    
    // Dispatch event for other components to refresh
    window.dispatchEvent(new CustomEvent('listChanged', { detail: { list: listValue } }));
  };

  const getCurrentDirectoryName = () => {
    if (!currentBookId && !isS3Mode) {
      // In local mode, try to get directory name from URL parameter
      const booksToEdit = searchParams.get('books-to-edit');
      if (booksToEdit) {
        // First check current directories state
        let directory = directories.find(d => d.name === booksToEdit);
        if (directory) {
          return directory.name;
        }
        // If not found, check localStorage
        try {
          const storedDirectoriesString = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
          if (storedDirectoriesString) {
            const storedDirectories: BookDirectory[] = JSON.parse(storedDirectoriesString);
            directory = storedDirectories.find(d => d.name === booksToEdit);
            if (directory) {
              return directory.name;
            }
          }
        } catch (error) {
          console.error('[BookFileSelector] Error parsing stored directories in getCurrentDirectoryName:', error);
        }
        // If found in URL but not in directories, return the URL param as fallback
        return booksToEdit;
      }
    }
    if (!currentBookId) return 'Select Books Directory';
    
    // Try to find directory by current book ID
    let directory = directories.find(d => currentBookId.startsWith(`list-${d.id}`));
    
    // If not found in current directories, try to get from URL parameter
    if (!directory) {
      const booksToEdit = searchParams.get('books-to-edit');
      if (booksToEdit && !isS3Mode) {
        // In local mode, URL param is directory name
        directory = directories.find(d => d.name === booksToEdit);
        // If still not found, check localStorage
        if (!directory) {
          try {
            const storedDirectoriesString = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
            if (storedDirectoriesString) {
              const storedDirectories: BookDirectory[] = JSON.parse(storedDirectoriesString);
              directory = storedDirectories.find(d => d.name === booksToEdit);
            }
          } catch (error) {
            console.error('[BookFileSelector] Error parsing stored directories in getCurrentDirectoryName:', error);
          }
        }
      }
    }
    
    return directory ? directory.name : 'Select Books Directory';
  };

  const getCurrentDirectoryInfo = () => {
    if (!currentBookId && !isS3Mode) {
      // In local mode, try to get directory info from URL parameter
      const booksToEdit = searchParams.get('books-to-edit');
      if (booksToEdit) {
        // First check current directories state
        let directory = directories.find(d => d.name === booksToEdit);
        if (directory) {
          return directory;
        }
        // If not found, check localStorage
        try {
          const storedDirectoriesString = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
          if (storedDirectoriesString) {
            const storedDirectories: BookDirectory[] = JSON.parse(storedDirectoriesString);
            directory = storedDirectories.find(d => d.name === booksToEdit);
            if (directory) {
              return directory;
            }
          }
        } catch (error) {
          console.error('[BookFileSelector] Error parsing stored directories in getCurrentDirectoryInfo:', error);
        }
      }
    }
    if (!currentBookId) return null;
    
    // Try to find directory by current book ID
    let directory = directories.find(d => currentBookId.startsWith(`list-${d.id}`));
    
    // If not found in current directories, try to get from URL parameter
    if (!directory) {
      const booksToEdit = searchParams.get('books-to-edit');
      if (booksToEdit && !isS3Mode) {
        // In local mode, URL param is directory name
        directory = directories.find(d => d.name === booksToEdit);
        // If still not found, check localStorage
        if (!directory) {
          try {
            const storedDirectoriesString = localStorage.getItem(STORAGE_KEYS.BOOK_DIRECTORIES);
            if (storedDirectoriesString) {
              const storedDirectories: BookDirectory[] = JSON.parse(storedDirectoriesString);
              directory = storedDirectories.find(d => d.name === booksToEdit);
            }
          } catch (error) {
            console.error('[BookFileSelector] Error parsing stored directories in getCurrentDirectoryInfo:', error);
          }
        }
      }
    }
    
    return directory;
  };

  return (
    <div className="book-file-selector">
      {isS3Mode ? (
        // S3 Mode: Show button for folder selection and dropdown for list selection
        <div className="s3-selectors">
          <div className="s3-folder-selector" ref={s3FolderDropdownRef}>
            <button
              className="book-selector-btn-compact"
              onClick={() => setIsS3FolderOpen(!isS3FolderOpen)}
              title="Select folder"
            >
              <div className="book-selector-content-compact">
                <span className="book-selector-title-compact">
                  📚 {s3FolderOptions.find(opt => opt.value === selectedS3Folder)?.label || 'Select Folder'}
                </span>
              </div>
            </button>
            {isS3FolderOpen && (
              <div className="s3-folder-dropdown-menu">
                {s3FolderOptions.map(option => (
                  <button
                    key={option.value}
                    className={`s3-folder-option ${selectedS3Folder === option.value ? 'active' : ''}`}
                    onClick={() => {
                      handleS3FolderChange(option.value);
                      setIsS3FolderOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {availableLists.length > 0 && (
            <div className="s3-list-selector">
              <select
                value={selectedList || '--none--'}
                onChange={(e) => handleListChange(e.target.value)}
                className="s3-list-dropdown"
                disabled={isLoadingLists}
              >
                <option value="--none--">--none selected--</option>
                <option value="ALL">--ALL--</option>
                {availableLists.map(list => (
                  <option key={list.filename} value={list.filename}>
                    {list.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      ) : (
        // Local Mode: Show directory selector and list selector (lists below folder)
        <div className="local-selectors">
          <button 
            className="book-selector-btn-compact"
            onClick={() => setIsOpen(!isOpen)}
            title={currentBookId ? `Current: ${getCurrentDirectoryName()}` : 'Select Books Directory'}
          >
            <div className="book-selector-content-compact">
              <span className="book-selector-title-compact">📚 {getCurrentDirectoryName()}</span>
              {getCurrentDirectoryInfo() && (
                <span className="book-selector-count-compact">
                  ({getCurrentDirectoryInfo()?.bookLists.reduce((sum, list) => sum + list.bookCount, 0)} books)
                </span>
              )}
            </div>
          </button>
          {availableLists.length > 0 && (
            <div className="local-list-selector">
              <select
                value={selectedList || '--none--'}
                onChange={(e) => handleListChange(e.target.value)}
                className="local-list-dropdown"
                disabled={isLoadingLists}
              >
                <option value="--none--">--none selected--</option>
                <option value="ALL">--ALL--</option>
                {availableLists.map(list => (
                  <option key={list.filename} value={list.filename}>
                    {list.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
      
      {isOpen && (
        <div className="book-selector-dropdown">
          <div className="dropdown-header">
            <h4>Select Books Directory</h4>
            <div className="header-actions">
              <div className="help-icon" title="Directory selection help">
                ℹ️
                <div className="help-tooltip">
                  <div className="tooltip-content">
                    <div className="tooltip-item">
                      <strong>How to use:</strong> Add directories individually or import from a JSON file.
                    </div>
                    <div className="tooltip-item">
                      <strong>Book list file:</strong> all-books.json, books.json, book-list.json, or catalog.json
                    </div>
                    <div className="tooltip-item">
                      <strong>Books directory:</strong> Should contain folders named with ISBNs
                    </div>
                    <div className="tooltip-item">
                      <strong>JSON format:</strong> {`{"directories": [{"path": "relative/path", "name": "Display Name"}]}`}
                    </div>
                  </div>
                </div>
              </div>
              <button 
                className="close-btn"
                onClick={() => setIsOpen(false)}
              >
                ×
              </button>
            </div>
          </div>
          
          <div className="dropdown-content">
            <div className="selection-actions">
              <button 
                className="add-directory-btn"
                onClick={() => setShowAddForm(true)}
                disabled={isScanning}
              >
                {isScanning ? '⏳ Scanning...' : '📁 Add Directory...'}
              </button>
              <button 
                className="import-json-btn"
                onClick={handleJsonImport}
                disabled={isScanning}
              >
                {isScanning ? '⏳ Importing...' : '📄 Import from JSON...'}
              </button>
            </div>
            
            {showAddForm && (
              <div className="add-directory-form">
                <h5>Add New Directory</h5>
                <div className="form-group">
                  <label>Directory Path:</label>
                  <input
                    type="text"
                    value={newDirectoryPath}
                    onChange={(e) => setNewDirectoryPath(e.target.value)}
                    placeholder="/path/to/your/books/directory"
                    disabled={isScanning}
                  />
                </div>
                <div className="form-group">
                  <label>Display Name:</label>
                  <input
                    type="text"
                    value={newDirectoryName}
                    onChange={(e) => setNewDirectoryName(e.target.value)}
                    placeholder="My Books Collection"
                    disabled={isScanning}
                  />
                </div>
                <div className="form-actions">
                  <button 
                    onClick={handleAddDirectory}
                    disabled={isScanning || !newDirectoryPath.trim() || !newDirectoryName.trim()}
                  >
                    {isScanning ? 'Scanning...' : 'Add Directory'}
                  </button>
                  <button 
                    onClick={() => {
                      setShowAddForm(false);
                      setNewDirectoryPath('');
                      setNewDirectoryName('');
                    }}
                    disabled={isScanning}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {filteredDirectories.length > 0 && (
              <div className="stored-directories">
                <h5>Saved Directories</h5>
                {filteredDirectories.map((directory) => (
                  <div key={directory.id} className="directory-group">
                    <div className="directory-header">
                      {editingName === directory.id ? (
                        <input
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={() => handleRenameSave(directory.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameSave(directory.id);
                            if (e.key === 'Escape') handleRenameCancel();
                          }}
                          className="rename-input"
                          autoFocus
                        />
                      ) : (
                        <>
                          <span className="directory-name">📁 {directory.name}</span>
                          <span className="directory-path">{directory.path}</span>
                          <span className="directory-count">
                            {directory.bookLists.reduce((sum, list) => sum + list.bookCount, 0)} books
                          </span>
                          <span className="directory-date">
                            {new Date(directory.addedAt).toLocaleDateString()}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="directory-actions">
                      {editingName !== directory.id && (
                        <button
                          className="rename-directory-btn"
                          onClick={(e) => handleRenameStart(directory.id, directory.name, e)}
                          title="Rename directory"
                        >
                          ✏️
                        </button>
                      )}
                      <button
                        className="delete-directory-btn"
                        onClick={(e) => handleDeleteDirectory(directory.id, e)}
                        title="Delete directory"
                      >
                        🗑️
                      </button>
                    </div>
                    
                    {directory.bookLists.length > 0 && (
                      <div className="book-lists">
                        {directory.bookLists.map((bookList, index) => (
                          <div
                            key={index}
                            className={`book-list-item ${currentBookId === `list-${directory.id}-${bookList.fileName}` ? 'active' : ''}`}
                            onClick={() => handleBookListSelect(directory, bookList)}
                          >
                            <span className="book-list-name">📖 {bookList.fileName}</span>
                            <span className="book-list-count">{bookList.bookCount} books</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

BookFileSelector.displayName = 'BookFileSelector';

// Memoize component to prevent unnecessary re-renders
export default React.memo(BookFileSelector, (prevProps, nextProps) => {
  // Only re-render if these props actually change
  return (
    prevProps.isS3Mode === nextProps.isS3Mode &&
    prevProps.currentBookId === nextProps.currentBookId &&
    prevProps.onBookFileSelected === nextProps.onBookFileSelected
  );
});